import React, { useState, useEffect } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import {
  Menu,
  X,
  LayoutDashboard,
  Package,
  ShoppingCart,
  Users,
  MessageSquare,
  Mail,
  Ticket,
  CreditCard,
  BarChart3,
  LogOut,
  Image,
  Settings,
  ChevronRight,
  User,
} from 'lucide-react'
import { useAuthStore } from '../lib/auth'

const navItems = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/products', label: 'Products', icon: Package },
  { path: '/categories', label: 'Categories', icon: BarChart3 },
  { path: '/carousels', label: 'Carousels', icon: Image },
  { path: '/orders', label: 'Orders', icon: ShoppingCart },
  { path: '/customers', label: 'Customers', icon: Users },
  { path: '/reviews', label: 'Reviews', icon: MessageSquare },
  { path: '/contact-messages', label: 'Contact Messages', icon: Mail },
  { path: '/coupons', label: 'Coupons', icon: Ticket },
  { path: '/payments', label: 'Payments', icon: CreditCard },
  { path: '/analytics', label: 'Analytics', icon: BarChart3 },
  { path: '/profile', label: 'Profile & Security', icon: User },
  { path: '/settings', label: 'Settings', icon: Settings },
]

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { logout } = useAuthStore()

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768
      setIsMobile(mobile)
      if (mobile) {
        setSidebarOpen(false)
      }
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const handleNavClick = (path: string) => {
    navigate(path)
    if (isMobile) {
      setMobileMenuOpen(false)
    }
  }

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`${
          sidebarOpen && !isMobile ? 'w-64' : isMobile && mobileMenuOpen ? 'w-64' : 'w-20'
        } ${
          isMobile && !mobileMenuOpen
            ? 'fixed -left-full'
            : isMobile
              ? 'fixed left-0 z-40'
              : 'relative'
        } top-0 h-screen bg-gradient-to-b from-[#6D3A0D] to-[#5D2A0D] text-white transition-all duration-300 flex flex-col overflow-hidden shadow-lg md:static`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-center border-b border-[#5D2A0D]">
          <div className="flex items-center gap-2">
            <div className="bg-[#F6EEE7] p-2 rounded-lg">
              <Package size={24} className="text-[#6D3A0D]" />
            </div>
            {(sidebarOpen || (isMobile && mobileMenuOpen)) && (
              <h1 className="font-bold text-lg">Te Amo</h1>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-6 px-3 space-y-2">
          {navItems.map(({ path, label, icon: Icon }) => {
            const isActive = location.pathname === path
            return (
              <button
                key={path}
                onClick={() => handleNavClick(path)}
                className={`w-full flex items-center px-4 py-3 rounded-lg transition-all duration-200 ${
                  isActive
                    ? 'bg-[#8B4513] text-white shadow-md'
                    : 'text-amber-100 hover:bg-[#F6EEE7]/10'
                }`}
                title={sidebarOpen && !isMobile ? '' : label}
              >
                <Icon size={20} className="flex-shrink-0" />
                {(sidebarOpen || (isMobile && mobileMenuOpen)) && (
                  <span className="ml-3 font-medium text-sm">{label}</span>
                )}
              </button>
            )
          })}
        </nav>

        {/* Logout */}
        <div className="border-t border-[#5D2A0D] p-4 space-y-3">
          <button
            onClick={handleLogout}
            className="w-full flex items-center px-4 py-2.5 text-amber-200 hover:bg-red-600/20 rounded-lg transition-colors"
            title={sidebarOpen && !isMobile ? '' : 'Logout'}
          >
            <LogOut size={20} className="flex-shrink-0" />
            {(sidebarOpen || (isMobile && mobileMenuOpen)) && (
              <span className="ml-3 font-medium text-sm">Logout</span>
            )}
          </button>

          {!isMobile && (
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="w-full flex items-center justify-center py-2 text-amber-200 hover:bg-[#F6EEE7]/10 rounded-lg transition-colors"
            >
              {sidebarOpen ? <X size={20} /> : <ChevronRight size={20} />}
            </button>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <div className="h-16 bg-white border-b border-[#F6EEE7] flex items-center justify-between px-4 md:px-8 shadow-sm">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Menu size={24} className="text-[#6D3A0D]" />
            </button>
            <h1 className="text-lg md:text-2xl font-bold bg-gradient-to-r from-[#6D3A0D] to-[#8B4513] bg-clip-text text-transparent">
              Admin
            </h1>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/profile')}
              className="flex items-center gap-2 hover:bg-[#F6EEE7]/60 px-3 py-1.5 rounded-xl transition border border-[#F6EEE7] cursor-pointer"
              title="Admin Profile & Security"
            >
              <div className="w-7 h-7 rounded-full bg-[#6D3A0D] text-white flex items-center justify-center font-bold text-xs shadow-2xs">
                A
              </div>
              <span className="text-xs font-bold text-gray-800 hidden sm:inline">Admin Profile</span>
            </button>
          </div>
        </div>

        {/* Page Content */}
        <div className="flex-1 overflow-auto">
          <div className="p-4 sm:p-6 md:p-8">
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  )
}
